import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';

/* -------------------------------------------------------------------------- */
/*  Shared modal logic                                                        */
/*  Used by BOTH the delegated `.mrk-quick-add-btn` button (collection cards and  */
/*  the Boost app) and the <quick-add-component> element (recommendations &   */
/*  upsells). Single source of truth - no duplicated fetch/render code.       */
/* -------------------------------------------------------------------------- */

/**
 * The curated quick-add template (templates/product.quick-view.json). It has
 * `layout: false` and only the product-information section, so ?view=quick-view
 * returns just the buy-box (no header/footer/other sections) and stays editable
 * in the theme customizer.
 */
const QUICK_ADD_VIEW = 'quick-view';

/* -------------------------------------------------------------------------- */
/*  Recently-viewed prints                                                     */
/*  Opening a print in the quick view counts as viewing it, exactly like       */
/*  landing on its PDP. Recorded HERE because this module is the single funnel */
/*  every quick view goes through - collection/home cards, recommendations and */
/*  the bundle-builder print selector - and it loads on every page, whereas    */
/*  bundle-builder.js only loads on PDPs that render the block.                */
/*  The key and the entry shape are the SAME contract as pushRecent() /        */
/*  recentAsCards() in assets/bundle-builder.js, which READS this list for the */
/*  print selector's "Recently Viewed" chip and swatch tab.                    */
/* -------------------------------------------------------------------------- */
const RECENT_KEY = 'drool_recent_prints';
const RECENT_MAX = 24;
/**
 * The Boost app's OWN recently-viewed list, which feeds its "Recently Viewed"
 * recommendation widgets (product page, collection page, cart). Boost writes this key
 * only when a real product page boots - `RecentlyViewedProductModule` in the app bundle
 * guards on `router.page === 'product'` - so quick views never reached it either.
 * Same format the app uses: a JSON array of product-id STRINGS, newest first.
 * Capped to match the widget's "Maximum recommendations" setting.
 */
const BOOST_RECENT_KEY = 'boostSDRecentlyViewedProduct';
const BOOST_RECENT_MAX = 24;
/** Fired after a quick view records a print, so an open print selector can refresh. */
const RECENT_EVENT = 'drool:recent-prints-updated';

/**
 * Reads the product a fetched quick view is showing. Everything is taken from the
 * fetched buy-box markup rather than the trigger, so every entry point works without
 * carrying product data on the button (the Boost app renders its own cards).
 * @param {Element} productGrid - the fetched [data-product-grid-content]
 * @param {string} productPageUrl
 * @returns {{ id: number | string, handle: string, title: string, image: string, ratio: string | null } | null}
 */
function readQuickViewProduct(productGrid, productPageUrl) {
  const rawId = productGrid.querySelector('[data-product-id]')?.getAttribute('data-product-id') || '';
  let handle = '';
  try {
    const path = new URL(productPageUrl, window.location.origin).pathname;
    handle = (path.split('/products/')[1] || '').split('/')[0];
  } catch (error) {
    /* malformed URL - caught by the guard below */
  }
  if (!rawId || !handle) return null;

  const title = productGrid.querySelector('.view-product-title a, .view-product-title, h1')?.textContent?.trim() || '';
  // `.product-media__image` is the print itself; the frame overlays sharing the gallery
  // are `.mrk-frame-image-item`. The lazyload copy holds the real URL on data-src and a
  // 1px placeholder in src, so data-src wins.
  const img = productGrid.querySelector('.product-media__image');
  let image = img?.getAttribute('data-src') || img?.getAttribute('src') || '';
  if (image && !/[?&]width=/.test(image)) image += `${image.includes('?') ? '&' : '?'}width=400`;
  // Cards take either a "W / H" pair or a bare aspect-ratio number (see recentAsCards).
  const ratio = (img?.getAttribute('style') || '').match(/aspect-ratio:\s*([\d.]+)/)?.[1] || null;

  return { id: Number(rawId) || rawId, handle, title, image, ratio };
}

/**
 * Mirrors the view into the Boost app's list so its "Recently Viewed" widget includes
 * quick-viewed prints, not just ones opened on their own PDP. Written in Boost's format
 * so the app keeps managing the list normally; nothing inside the app changes.
 * The widget reads this key once at page bootstrap, so a print quick-viewed here appears
 * on the NEXT page load rather than in the carousel behind the open modal.
 * @param {number | string} id - Shopify product id
 */
function recordBoostRecentlyViewed(id) {
  if (!id) return;
  try {
    const stored = JSON.parse(localStorage.getItem(BOOST_RECENT_KEY) || '[]');
    const list = (Array.isArray(stored) ? stored : []).map(String).filter((p) => p !== String(id));
    list.unshift(String(id));
    localStorage.setItem(BOOST_RECENT_KEY, JSON.stringify(list.slice(0, BOOST_RECENT_MAX)));
  } catch (error) {
    /* storage full / disabled */
  }
}

/**
 * Puts the quick-viewed print at the front of the shared recently-viewed list.
 * No-ops when storage is unavailable (private mode / quota) - the list is a nicety
 * and must never block the modal.
 * @param {Element} productGrid
 * @param {string} productPageUrl
 */
function recordRecentPrint(productGrid, productPageUrl) {
  const entry = readQuickViewProduct(productGrid, productPageUrl);
  if (!entry) return;
  try {
    const stored = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    const list = Array.isArray(stored) ? stored : [];
    // Dedupe on BOTH keys: the same print can already be in the list from its own PDP.
    const deduped = list.filter((p) => p && String(p.id) !== String(entry.id) && p.handle !== entry.handle);
    deduped.unshift(entry);
    localStorage.setItem(RECENT_KEY, JSON.stringify(deduped.slice(0, RECENT_MAX)));
    document.dispatchEvent(new CustomEvent(RECENT_EVENT));
  } catch (error) {
    /* storage full / disabled */
  }
  recordBoostRecentlyViewed(entry.id);
}

/**
 * Fetches the buy-box: the lean quick-view template first, falling back to the
 * full product page. (The buy-box is extracted from it in injectBuyBox.)
 * @param {string} productPageUrl
 * @param {AbortSignal} [signal]
 * @returns {Promise<Document | null>}
 */
async function fetchBuyBox(productPageUrl, signal) {
  if (!productPageUrl) return null;

  const quickView = await fetchQuickView(productPageUrl, signal);
  if (quickView) {
    window.mrkQuickAddView = QUICK_ADD_VIEW;
    return quickView;
  }

  // Fallback: full product page (e.g. if the quick-view template isn't present).
  window.mrkQuickAddView = '';
  const response = await fetch(productPageUrl, { signal });
  if (!response.ok) {
    throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
  }
  return new DOMParser().parseFromString(await response.text(), 'text/html');
}

/**
 * Fetches ?view=quick-view and returns it only if it rendered the buy-box.
 * @param {string} productPageUrl
 * @param {AbortSignal} [signal]
 * @returns {Promise<Document | null>}
 */
async function fetchQuickView(productPageUrl, signal) {
  try {
    const url = new URL(productPageUrl, window.location.origin);
    url.searchParams.set('view', QUICK_ADD_VIEW);

    const response = await fetch(url.toString(), { signal });
    if (!response.ok) return null;

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    return doc.querySelector('[data-product-grid-content]') ? doc : null;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') throw error;
    return null;
  }
}

/**
 * Injects the fetched buy-box into the modal and boots the framing engine.
 * @param {Document} newHtml
 * @param {string} productPageUrl
 */
function injectBuyBox(newHtml, productPageUrl) {
  const productGrid = newHtml.querySelector('[data-product-grid-content]');
  const modalContent = document.getElementById('quick-add-modal-content');

  if (!productGrid || !modalContent) return;

  // Opening a print in the quick view counts as viewing it (Recently Viewed). Read from
  // the FETCHED grid before morph moves its nodes into the modal.
  recordRecentPrint(productGrid, productPageUrl);

  // Framing products (the custom mrk buy-box) carry a variant-data script;
  // simple products (single default variant, no variant picker) don't. We inject
  // the whole .product-details for both and let CSS lay it out.
  const isFramingProduct = productGrid.querySelector('#mrk-main-variant-data') != null;

  morph(modalContent, productGrid);

  // Point the modal's "See full details" link at this product's PDP.
  const seeDetailsLink = document.querySelector('#quick-add-dialog .drool-quick-see-details');
  if (seeDetailsLink instanceof HTMLAnchorElement && productPageUrl) seeDetailsLink.href = productPageUrl;

  if (isFramingProduct) {
    // Capture the real product main section id (template--123__main) from the
    // fetched page's wrapper so mrk.js can refresh the frame picker on size
    // change with a working URL.
    const sectionWrapper = productGrid.closest('.shopify-section');
    window.mrkQuickAddSectionId = sectionWrapper?.id?.replace('shopify-section-', '') || '';

    // The host page may carry its OWN mrk buy-box - any product page, whether the
    // modal was opened from the bundle builder (pack mode) or from a
    // recommendation/upsell card. mrk.js drives the buy-box through page-global
    // selectors ($('#mrk-main-variant-data'), '.mrk-frame-switcher', ...) which
    // resolve to the FIRST match in the DOM - i.e. the host product, not the
    // modal. That made the modal resolve the HOST's framing mode (e.g. the
    // framing service never appeared for a child-frame product quick-added on a
    // product page). So on EVERY open: snapshot the host globals before the
    // injected inline scripts overwrite them, and lift the host's colliding
    // buy-box elements out of the DOM for the modal's lifetime (both restored
    // by the dialog-close handler).
    const dialog = document.getElementById('quick-add-dialog');
    if (dialog) {
      const hostHasBuyBox = Array.from(document.querySelectorAll(BB_MAIN_BUYBOX_SELECTOR)).some(
        (el) => !dialog.contains(el)
      );
      if (hostHasBuyBox) {
        bbSaveHostGlobals();
        bbDetachMain(dialog);
      }
    }

    // <script> nodes injected via morph are inert; re-execute the inline data
    // scripts (window.main_product_data, framing globals, #mrk-main-variant-data)
    // so they reflect THIS product, then run the shared mrk bootstrap.
    activateScripts(modalContent);
    if (typeof window.mrkInitQuickAdd === 'function') window.mrkInitQuickAdd();
  }

  // The buy-box is in place and booted. wd-quick-add-free.js listens for this to offer a free print.
  modalContent.dispatchEvent(new CustomEvent('drool:quick-add-ready', { bubbles: true }));
}

/**
 * Re-executes inline executable <script>s inside an injected container. Scripts
 * inserted via morph/DOMParser are flagged non-executing by the HTML spec, so we
 * clone each into a fresh element to run it. JSON/data scripts (e.g.
 * #mrk-main-variant-data) are read via textContent and must NOT run.
 * @param {HTMLElement} container
 */
function activateScripts(container) {
  for (const oldScript of container.querySelectorAll('script')) {
    const type = (oldScript.getAttribute('type') || '').toLowerCase();
    const isExecutable =
      type === '' || type === 'text/javascript' || type === 'application/javascript' || type === 'module';
    if (!isExecutable) continue;

    const newScript = document.createElement('script');
    for (const attr of Array.from(oldScript.attributes)) newScript.setAttribute(attr.name, attr.value);
    newScript.textContent = oldScript.textContent;
    oldScript.replaceWith(newScript);
  }
}

/* -------------------------------------------------------------------------- */
/*  Bundle-builder "pack mode" buy-box scoping                                 */
/*  When the quick view is opened from the bundle builder it runs on a PRODUCT */
/*  page that already has its own buy-box. The mrk buy-box JS uses page-global */
/*  selectors + globals (built for one product per page), so injecting a       */
/*  second one collides. For pack mode we (a) snapshot the host page's product */
/*  globals so the inject doesn't corrupt the main product, and (b) run init   */
/*  with the host's colliding buy-box elements briefly lifted out so the        */
/*  global selectors resolve to the MODAL's elements. (Full interactive        */
/*  scoping of every change handler is a later step.)                          */
/* -------------------------------------------------------------------------- */

/** Product globals the injected buy-box inline scripts overwrite (see snippets). */
const BB_HOST_GLOBAL_KEYS = [
  'main_product_data',
  'free_framing_service',
  'show_default_black_variant',
  'FrameType',
  'availableFrameIDs',
  'child_frame_sizes',
  'framed_print_only_sizes',
  'framed_print_only_variant_ids',
  'child_frame_variant_ids',
  'framing_service_locked_variant_ids',
  'droolFreeFramingVariantIds',
  'drool_fulfilment',
  'drool_child_frames_only',
  'mrk_unframed_value',
];
/** @type {Record<string, any> | null} */
let bbHostGlobals = null;

/** Snapshot host-page product globals BEFORE the injected scripts overwrite them. */
function bbSaveHostGlobals() {
  bbHostGlobals = {};
  for (const key of BB_HOST_GLOBAL_KEYS) bbHostGlobals[key] = window[key];
}

/** Restore host-page product globals (called when the pack quick view closes). */
function bbRestoreHostGlobals() {
  if (!bbHostGlobals) return;
  for (const key of BB_HOST_GLOBAL_KEYS) window[key] = bbHostGlobals[key];
  bbHostGlobals = null;
}

/**
 * The host PDP's native buy-box elements that mrk.js's page-global selectors target.
 * While the pack quick view is open these are lifted OUT of the DOM so every mrk.js
 * operation (init AND every change-event cascade) resolves to the MODAL's copies —
 * otherwise the main product (first in the DOM) wins and its logic removes/rebuilds
 * the modal's pickers a few ms after they render.
 */
const BB_MAIN_BUYBOX_SELECTOR =
  'variant-picker, frame-variant-picker, #mrk-main-variant-data, .wd-native-picker, .mrk-product-form-wrapper, .mrk-hidden-inputs, .mrk-framing-service-wrapper, .free-framing-service, .wd-framing-locked-note';

/** @type {{ el: Element, placeholder: Comment }[] | null} */
let bbMainStash = null;

/**
 * Lift the host PDP's native buy-box elements out of the DOM (placeholder swap) for
 * the WHOLE time the pack quick view is open. No-op if already lifted.
 * @param {HTMLElement} dialog
 */
function bbDetachMain(dialog) {
  if (bbMainStash) return; // already lifted (e.g. re-open before close)
  bbMainStash = [];
  document.querySelectorAll(BB_MAIN_BUYBOX_SELECTOR).forEach((el) => {
    if (dialog.contains(el)) return; // keep the modal's own copies
    const placeholder = document.createComment('bb-qv-main');
    if (el.parentNode) {
      el.parentNode.replaceChild(placeholder, el);
      bbMainStash.push({ el, placeholder });
    }
  });
}

/** Put the host PDP's native buy-box elements back exactly where they were. */
function bbReattachMain() {
  if (!bbMainStash) return;
  for (const { el, placeholder } of bbMainStash) {
    if (placeholder.parentNode) placeholder.parentNode.replaceChild(el, placeholder);
  }
  bbMainStash = null;
}

/** Shows/hides the modal's loading overlay. @param {boolean} show */
function toggleLoader(show) {
  document.querySelector('#quick-add-dialog [data-quick-add-loader]')?.toggleAttribute('hidden', !show);
}

/** @returns {QuickAddDialog | null} */
function getDialog() {
  const dialog = document.getElementById('quick-add-dialog');
  // Duck-typed rather than `instanceof QuickAddDialog`. If this module is ever evaluated twice —
  // an import-map miss is enough — the element is upgraded against the FIRST class object and
  // `instanceof` against the second returns false even though the dialog works perfectly.
  return dialog && typeof (/** @type {any} */ (dialog).showDialog) === 'function'
    ? /** @type {any} */ (dialog)
    : null;
}

/**
 * The dialog, waiting for its custom-element upgrade if the tap got there first.
 *
 * quick-add.js is loaded `type="module" fetchpriority="low"` (snippets/scripts.liquid) and pulls in
 * five more modules through the import map, so a tap can land AFTER the delegated listener is bound
 * but BEFORE customElements.define('quick-add-dialog') has upgraded the element. iOS in-app
 * browsers (WKWebView — Instagram, Facebook, TikTok) are the worst case for that: low priority,
 * memory pressure, and the page often resumes mid-load after a scroll.
 *
 * The old code returned null there and the tap did nothing at all — no modal, no error, no log,
 * and (because the listener had already called preventDefault) no navigation either. Dead button
 * for the rest of the page, which is exactly the reported symptom.
 * @returns {Promise<any|null>}
 */
async function getDialogReady() {
  const ready = getDialog();
  if (ready) return ready;
  // Nothing to wait for if the element itself was never rendered.
  if (!document.getElementById('quick-add-dialog')) return null;
  try {
    await customElements.whenDefined('quick-add-dialog');
  } catch (error) {
    return null;
  }
  return getDialog();
}

/* -------------------------------------------------------------------------- */
/*  Delegated `.mrk-quick-add-btn` trigger                                        */
/*  A plain button carrying data-product-url. Works in the theme cards AND in */
/*  the Boost app (no web component / Horizon `on:` binding needed).          */
/* -------------------------------------------------------------------------- */

/** @type {Map<string, Document>} Parsed buy-box docs cached by product URL. */
const buyBoxCache = new Map();
/** @type {Map<string, Promise<Document | null>>} In-flight buy-box fetches, keyed by product URL. */
const buyBoxPending = new Map();
/** @type {AbortController | null} */
let buttonAbortController = null;
/** @type {string} The product URL `buttonAbortController` belongs to. */
let buttonAbortUrl = '';

/**
 * The parsed buy-box for a product URL: a cache hit, a request already in flight, or a new one.
 * Deduped by URL so the pointerdown warm-up below and the tap that follows it share ONE request
 * instead of racing two.
 * @param {string} productPageUrl
 * @param {AbortSignal} [signal]
 * @returns {Promise<Document | null>}
 */
function loadBuyBox(productPageUrl, signal) {
  const cached = buyBoxCache.get(productPageUrl);
  if (cached) return Promise.resolve(cached);
  const inFlight = buyBoxPending.get(productPageUrl);
  if (inFlight) return inFlight;
  const request = fetchBuyBox(productPageUrl, signal)
    .then((doc) => {
      if (doc) buyBoxCache.set(productPageUrl, doc);
      return doc;
    })
    .finally(() => {
      if (buyBoxPending.get(productPageUrl) === request) buyBoxPending.delete(productPageUrl);
    });
  buyBoxPending.set(productPageUrl, request);
  // Marks the stored promise as handled so a warm-up nobody awaits can't raise an unhandled
  // rejection. Callers that DO await it still receive the error.
  request.catch(() => {});
  return request;
}

/**
 * Opens the quick-add modal for a product URL: shows the modal immediately with
 * a loading overlay, fetches + renders in the background, then reveals it.
 * @param {string} productPageUrl
 */
async function openQuickAdd(productPageUrl) {
  if (!productPageUrl) return;

  // Start the network work FIRST, then wait for the dialog. These used to run one after the other,
  // so the buy-box fetch did not even begin until the custom element had upgraded — and on a slow
  // in-app browser that upgrade is exactly what takes the time. Overlapping them removes a whole
  // round trip from the wait the shopper actually feels.
  let doc = buyBoxCache.get(productPageUrl);
  const needsFetch = !doc;
  let request = null;
  if (needsFetch) {
    // A tap on a DIFFERENT product supersedes one still loading. Same product (a double tap, or
    // the pointerdown warm-up below): reuse the request in flight rather than aborting our own.
    if (buttonAbortController && buttonAbortUrl !== productPageUrl) {
      buttonAbortController.abort();
      buyBoxPending.delete(buttonAbortUrl); // that promise is rejected now — never hand it out
      buttonAbortController = null;
      buttonAbortUrl = '';
    }
    if (buyBoxPending.has(productPageUrl)) {
      request = loadBuyBox(productPageUrl);
    } else {
      buttonAbortController = new AbortController();
      buttonAbortUrl = productPageUrl;
      request = loadBuyBox(productPageUrl, buttonAbortController.signal);
    }
  }

  const dialog = await getDialogReady();
  if (!dialog) {
    // Never fail silently again — this bug left no trace at all, which is why it was
    // unreproducible. If this ever logs, the dialog element or its upgrade is the problem.
    console.warn('[quick-add] dialog unavailable — ignoring open for', productPageUrl);
    return;
  }

  // Show the loader BEFORE opening the dialog: on mobile the dialog only gets
  // its height from the "loader visible" CSS rule, so opening first would paint
  // a zero-height (invisible) box on the first, uncached open.
  if (needsFetch) toggleLoader(true);

  dialog.showDialog();

  try {
    if (request) {
      try {
        doc = await request;
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
        throw error;
      } finally {
        if (buttonAbortUrl === productPageUrl) {
          buttonAbortController = null;
          buttonAbortUrl = '';
        }
      }
    }

    if (doc) {
      const fresh = new DOMParser().parseFromString(doc.documentElement.outerHTML, 'text/html');
      injectBuyBox(fresh, productPageUrl);
    }
  } finally {
    toggleLoader(false);
  }
}

/**
 * Opens the quick-add modal from OUTSIDE this module (the bundle builder). When
 * `pack` is set, the dialog is flagged `data-bb-pack`, which (via CSS in
 * quick-add-modal.liquid) hides the native Add-to-cart / framing service and reveals
 * the quick-view template's "Add to pack" CTA instead. Every normal open path clears
 * the flag first (below) so it never leaks into a collection-page quick add.
 * @param {string} productPageUrl
 * @param {{ pack?: boolean }} [options]
 */
window.mrkOpenQuickAdd = function (productPageUrl, options) {
  // Plain attribute write on the raw element: the pack flag must land even if the element has
  // not been upgraded yet, because openQuickAdd below now waits for that upgrade rather than
  // bailing, and the flag has to be set before the content is injected.
  document.getElementById('quick-add-dialog')?.toggleAttribute('data-bb-pack', !!(options && options.pack));
  return openQuickAdd(productPageUrl);
};

// Delegated on `document` so it works for buttons added at any time (incl. the
// Boost app rendering cards after page load - no per-button binding needed).
// Capture phase + stopPropagation so the button wins over the surrounding card
// link/handlers (e.g. Boost's product link) and opens the modal instead of
// navigating to the PDP.
document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('.mrk-quick-add-btn');
    if (!button) return;

    // Only swallow the tap if there is something to open. When the dialog element is missing
    // outright, fall through to the card's own link so the shopper still reaches the PDP —
    // the old unconditional preventDefault turned that case into a completely dead button.
    const el = document.getElementById('quick-add-dialog');
    if (!el) {
      console.warn('[quick-add] #quick-add-dialog missing — letting the tap through to the PDP');
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    // Plain attribute write, so it works even before the element is upgraded.
    el.toggleAttribute('data-bb-pack', false); // never inherit the bundle "pack mode"
    openQuickAdd(button.getAttribute('data-product-url') || '');
  },
  true
);

// Warm the buy-box the moment the finger lands, before the tap has even completed. A touch device
// leaves 100-300ms between `pointerdown` and `click` (longer in an iOS in-app browser, which waits
// to rule out a scroll), and that idle time is enough to have the product most of the way loaded.
// loadBuyBox dedupes by URL, so the click that follows joins this request instead of starting a
// second one. Passive + capture: this never calls preventDefault, so it cannot affect scrolling.
document.addEventListener(
  'pointerdown',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('.mrk-quick-add-btn');
    // The bundle builder's print-selector eye is NOT a .mrk-quick-add-btn — it opens the same
    // modal through window.mrkOpenQuickAdd, so it was the one trigger getting no head start at
    // all. Its product comes from the card's data-handle, the same value _openQuickView uses.
    const eye = button ? null : target.closest('.bundle-print-selector__expand');
    const card = eye && eye.closest('.bundle-print-selector__card');
    const handle = card ? card.getAttribute('data-handle') || '' : '';
    const url = button
      ? button.getAttribute('data-product-url') || ''
      : (handle ? '/products/' + handle : '');
    if (!url || buyBoxCache.has(url)) return;
    // A warm-up that fails is not an error: the click path retries and reports for itself.
    loadBuyBox(url).catch(() => {});
  },
  { capture: true, passive: true }
);

// Replay a tap that landed BEFORE this module executed. quick-add.js is `type="module"
// fetchpriority="low"` and pulls five more modules through the import map, so on a slow connection
// the delegated listener above simply does not exist yet — there is nothing to swallow the tap and
// nothing to open the modal, which is the "dead button" symptom in iOS in-app browsers. The tiny
// classic script in snippets/quick-add-modal.liquid runs during page parse, long before any module,
// and records the intent in `droolQuickAddPending` for us to act on here. By this line the listener
// is bound, so this only ever fires for that early window.
if (typeof window.droolQuickAddPending === 'string' && window.droolQuickAddPending) {
  const pendingUrl = window.droolQuickAddPending;
  window.droolQuickAddPending = null;
  document.getElementById('quick-add-dialog')?.toggleAttribute('data-bb-pack', false);
  openQuickAdd(pendingUrl);
}

// "See full details" always opens the PDP in SINGLE view: the PDP's bundle builder
// initialises from `drool_view_mode`, which at this point may hold a pack tier (e.g.
// the quick view was opened from an active pack flow). Stamp `single` at click time —
// delegated on `document` like the button above, since the link is injected with the
// quick-view content.
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  if (!target.closest('.mrk-quickview-see-details')) return;
  try { sessionStorage.setItem('drool_view_mode', 'single'); } catch (e) { /* storage unavailable */ }
});

// The modal layout differs between mobile and desktop, so close it when the
// breakpoint changes (matches the previous quick-add-component behaviour).
mediaQueryLarge.addEventListener('change', () => getDialog()?.closeDialog());

/* -------------------------------------------------------------------------- */
/*  <quick-add-component> (recommendations & upsells)                         */
/*  Unchanged trigger behaviour; reuses the shared modal logic above.         */
/* -------------------------------------------------------------------------- */

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Document | null} */
  #cachedProductHtml = null;

  get cachedProductHtml() {
    return this.#cachedProductHtml;
  }

  get productPageUrl() {
    return /** @type {HTMLAnchorElement} */ (this.closest('product-card')?.querySelector('a[ref="productCardLink"]'))
      ?.href;
  }

  connectedCallback() {
    super.connectedCallback();
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const dialog = getDialog();
    if (!dialog) return;
    dialog.toggleAttribute('data-bb-pack', false); // recommendations/upsells are never pack mode

    this.toggleAttribute('stay-visible', true);
    dialog.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });

    // Show the loader before opening so the dialog has height on first paint
    // (see openQuickAdd - mobile height comes from the loader-visible rule).
    const needsFetch = !this.#cachedProductHtml;
    if (needsFetch) toggleLoader(true);

    dialog.showDialog();

    try {
      if (needsFetch) {
        this.#abortController?.abort();
        this.#abortController = new AbortController();
        try {
          this.#cachedProductHtml = await fetchBuyBox(this.productPageUrl, this.#abortController.signal);
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') return;
          throw error;
        } finally {
          this.#abortController = null;
        }
      }

      if (this.#cachedProductHtml) {
        const fresh = new DOMParser().parseFromString(this.#cachedProductHtml.documentElement.outerHTML, 'text/html');
        injectBuyBox(fresh, this.productPageUrl);
      }
    } finally {
      toggleLoader(false);
    }
  };

  #closeQuickAddModal = () => {
    getDialog()?.closeDialog();
  };
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    // Pack mode (bundle builder): put the host PDP's buy-box back in the DOM, restore
    // its globals, and drop the pack flag, so the main product is unaffected afterwards.
    bbReattachMain();
    bbRestoreHostGlobals();
    this.removeAttribute('data-bb-pack');

    // Drop the injected buy-box now that the dialog is hidden (DialogCloseEvent fires
    // after dialog.close()). Otherwise its #mrk-main-variant-data duplicates the host's
    // restored copy and its stale pickers/inputs keep matching mrk.js's unscoped
    // selectors, corrupting host reads until the next open replaces the content.
    document.getElementById('quick-add-modal-content')?.replaceChildren();

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
